import { runtimeAccessConfig } from '@/config/runtimeAccess';
import { useAuth, useOauth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import { authService, type OAuthContext } from '@common/api/authService';
import type { OAuthAuthorizeCallbackTarget } from '@common/api/authService/oauthAuthorizeCallback';
import { useEffect, useRef, useState } from 'react';
import {
    isBustlyDesktopAuthCallbackUrl,
    isBustlyMcpBridgeCallbackUrl,
    resolveBustlyDesktopOAuthContext,
    resolveOAuthCallbackHandoffKind,
    type OAuthCallbackHandoffKind,
} from './desktopOAuthHandoff';

const PENDING_OAUTH_REDIRECT_KEY = 'bustly_pending_oauth_redirect';
export const BUSTLY_DOWNLOAD_URL = 'https://www.bustly.shop/download';

type UseBustlyDesktopOpenOptions = {
    enableAutomaticFlow?: boolean;
    onManualComplete?: () => void;
    oauthReturnContext?: OAuthContext | null;
};

type ContinueToBustlyOptions = {
    force?: boolean;
};

export const useBustlyDesktopOpen = ({
    enableAutomaticFlow = false,
    onManualComplete,
    oauthReturnContext,
}: UseBustlyDesktopOpenOptions = {}) => {
    const { t } = useTranslation();
    const { currentWorkspace, profile } = useAuth();
    const { getOAuthContext } = useOauth();
    const [redirectUrl, setRedirectUrl] = useState<string | null>(() => {
        if (typeof window === 'undefined') {
            return null;
        }
        return window.sessionStorage.getItem(PENDING_OAUTH_REDIRECT_KEY);
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const autoRedirectStartedRef = useRef(false);
    const autoAuthorizeStartedRef = useRef<string | null>(null);
    const callbackTriggeredRef = useRef(false);
    const oauthResolveInFlightRef = useRef<string | null>(null);

    const generatedOAuthContextRef = useRef<OAuthContext | null>(null);
    const storedOAuthContext = getOAuthContext();
    const hasValidOAuthReturnContext = Boolean(
        oauthReturnContext?.client_id?.trim() && oauthReturnContext?.login_trace_id?.trim()
    );
    const hasValidStoredOAuthContext = Boolean(
        storedOAuthContext?.client_id?.trim() && storedOAuthContext?.login_trace_id?.trim()
    );
    if (!generatedOAuthContextRef.current) {
        generatedOAuthContextRef.current = resolveBustlyDesktopOAuthContext({
            returnContext: oauthReturnContext,
            storedContext: storedOAuthContext,
            configuredProtocol: import.meta.env.VITE_BUSTLY_APP_PROTOCOL,
            createLoginTraceId: () => window.crypto.randomUUID(),
        });
    }
    const oauthContext = hasValidOAuthReturnContext || hasValidStoredOAuthContext
        ? resolveBustlyDesktopOAuthContext({
            returnContext: oauthReturnContext,
            storedContext: storedOAuthContext,
            configuredProtocol: import.meta.env.VITE_BUSTLY_APP_PROTOCOL,
            createLoginTraceId: () => generatedOAuthContextRef.current?.login_trace_id || window.crypto.randomUUID(),
        })
        : generatedOAuthContextRef.current;
    const workspaceId = currentWorkspace?.id || profile?.workspace?.id || null;
    const disableCloudRuntime = runtimeAccessConfig.disableCloudRuntime;
    const pendingDesktopOAuthContext = hasValidOAuthReturnContext
        ? oauthReturnContext
        : storedOAuthContext;
    const hasPendingDesktopOAuthContext = Boolean(
        pendingDesktopOAuthContext?.client_id
        && pendingDesktopOAuthContext?.login_trace_id
        && (pendingDesktopOAuthContext.redirect_uri || pendingDesktopOAuthContext.fallback_redirect_uri)
    );
    const shouldUseContinuationFlow = true;
    const oauthRequestKey = workspaceId && oauthContext
        ? [
            workspaceId,
            oauthContext.client_id,
            oauthContext.redirect_uri || '',
            oauthContext.fallback_redirect_uri || '',
            oauthContext.device_id || '',
            oauthContext.login_trace_id,
        ].join('::')
        : null;
    const invokeBustlyAuthDeepLink = (url: string): void => {
        if (typeof window === 'undefined') {
            return;
        }
        console.log('[useBustlyDesktopOpen] Invoking Bustly auth callback deep link', {
            workspaceId,
            hasPendingDesktopOAuthContext,
            loginTraceId: oauthContext?.login_trace_id || null,
        });
        const fallbackTimer = window.setTimeout(() => {
            if (document.visibilityState === 'visible') {
                console.log('[useBustlyDesktopOpen] Bustly Desktop did not open; redirecting to download page', {
                    downloadUrl: BUSTLY_DOWNLOAD_URL,
                });
                window.location.href = BUSTLY_DOWNLOAD_URL;
            }
        }, 1500);
        const cancelDownloadFallback = () => {
            window.clearTimeout(fallbackTimer);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', cancelDownloadFallback);
            window.removeEventListener('blur', cancelDownloadFallback);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                cancelDownloadFallback();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', cancelDownloadFallback, { once: true });
        window.addEventListener('blur', cancelDownloadFallback, { once: true });
        window.location.href = url;
    };

    const continueToBustly = async (
        targetUrl?: string | null,
        options?: ContinueToBustlyOptions,
    ): Promise<OAuthCallbackHandoffKind | null> => {
        const nextUrl = targetUrl?.trim();
        if (!nextUrl) {
            return null;
        }

        if (callbackTriggeredRef.current && !options?.force) {
            console.log('[useBustlyDesktopOpen] Skipping callback trigger because one has already been sent', {
                nextUrl,
            });
            return null;
        }

        callbackTriggeredRef.current = true;

        try {
            const parsedUrl = new URL(nextUrl);
            const isMcpBridgeCallback = isBustlyMcpBridgeCallbackUrl(nextUrl);
            const handoffKind = resolveOAuthCallbackHandoffKind(nextUrl);
            if (!handoffKind) {
                return null;
            }
            console.log('[useBustlyDesktopOpen] Preparing desktop OAuth callback handoff', {
                nextUrl,
                protocol: parsedUrl.protocol,
                host: parsedUrl.hostname,
                pathname: parsedUrl.pathname,
                isMcpBridgeCallback,
                via: handoffKind,
                loginTraceId: parsedUrl.searchParams.get('state') || parsedUrl.searchParams.get('login_trace_id'),
                hasCode: Boolean(parsedUrl.searchParams.get('code')),
            });
            if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
                invokeBustlyAuthDeepLink(nextUrl);
                window.sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
                return handoffKind;
            }
            if (isMcpBridgeCallback) {
                // The MCP bridge will redirect once more to ChatGPT's callback.
                // This must be a document navigation, not a background request.
                window.sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
                window.location.replace(nextUrl);
                return handoffKind;
            }
            await fetch(nextUrl, {
                method: 'GET',
                mode: 'no-cors',
                credentials: 'omit',
                cache: 'no-store',
                keepalive: true,
            });
            window.sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
            return handoffKind;
        } catch (callbackError) {
            callbackTriggeredRef.current = false;
            console.error('[useBustlyDesktopOpen] Failed to trigger desktop OAuth callback:', callbackError);
            return null;
        }
    };

    const resolveRedirectUrl = async (
        preferredTarget: OAuthAuthorizeCallbackTarget,
    ): Promise<string | null> => {
        if (!workspaceId || !oauthContext) {
            return redirectUrl;
        }

        setIsLoading(true);
        setError(null);

        try {
            const nextRedirectUrl = await authService.getOAuthAuthorizeRedirect(
                workspaceId,
                preferredTarget,
                oauthContext,
            );
            console.log('[useBustlyDesktopOpen] Resolved OAuth redirect target', {
                workspaceId,
                nextRedirectUrl,
                preferredTarget,
                loginTraceId: oauthContext.login_trace_id,
            });
            if (!nextRedirectUrl) {
                return null;
            }

            window.sessionStorage.setItem(PENDING_OAUTH_REDIRECT_KEY, nextRedirectUrl);
            setRedirectUrl(nextRedirectUrl);
            return nextRedirectUrl;
        } catch (resolveError: any) {
            console.error('[useBustlyDesktopOpen] Failed to resolve OAuth redirect URL:', resolveError);
            setError(resolveError?.message || t('website.clientAuth.prepareDesktopHandoffFailed'));
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenBustlyClick = async (): Promise<boolean> => {
        console.log('[useBustlyDesktopOpen] Open Bustly clicked', {
            disableCloudRuntime,
            workspaceId,
            redirectUrl,
            oauthRequestKey,
            hasOauthContext: Boolean(oauthContext),
            hasPendingDesktopOAuthContext,
            shouldUseContinuationFlow,
            loginTraceId: oauthContext?.login_trace_id || null,
        });

        if (!oauthRequestKey || !workspaceId || !oauthContext) {
            const nextError = 'Missing workspace context. Please refresh this page and try again.';
            console.warn('[useBustlyDesktopOpen] Cannot authorize Open Bustly because workspace context is missing', {
                workspaceId,
                hasOauthContext: Boolean(oauthContext),
                oauthRequestKey,
            });
            setError(nextError);
            setIsLoading(false);
            return false;
        }

        if (oauthResolveInFlightRef.current === oauthRequestKey) {
            console.log('[useBustlyDesktopOpen] Skipping manual Open Bustly authorize because a request is already in flight', {
                oauthRequestKey,
            });
            return false;
        }

        oauthResolveInFlightRef.current = oauthRequestKey;
        callbackTriggeredRef.current = false;
        try {
            const nextRedirectUrl = await resolveRedirectUrl('redirect');
            if (nextRedirectUrl) {
                const isDesktopCallback = isBustlyDesktopAuthCallbackUrl(nextRedirectUrl);
                const isMcpBridgeCallback = isBustlyMcpBridgeCallbackUrl(nextRedirectUrl);
                if (!isDesktopCallback && !isMcpBridgeCallback) {
                    setError('Bustly authorization callback is invalid. Please try again.');
                    return false;
                }
                const handoffKind = await continueToBustly(nextRedirectUrl, { force: true });
                if (handoffKind) {
                    onManualComplete?.();
                    return true;
                }
            }
            setError('Failed to trigger Bustly desktop authorization callback.');
            return false;
        } finally {
            if (oauthResolveInFlightRef.current === oauthRequestKey) {
                oauthResolveInFlightRef.current = null;
            }
        }
    };

    useEffect(() => {
        if (!enableAutomaticFlow) {
            return;
        }

        if (disableCloudRuntime) {
            console.log('[useBustlyDesktopOpen] Running OAuth success flow with cloud runtime disabled', {
                oauthRequestKey,
                redirectUrl,
                error,
            });
            if (!oauthRequestKey && !redirectUrl) {
                setIsLoading(false);
                return;
            }

            if (redirectUrl && !autoRedirectStartedRef.current) {
                autoRedirectStartedRef.current = true;
                void continueToBustly(redirectUrl);
                return;
            }

            if (!oauthRequestKey || error) {
                return;
            }

            if (
                autoAuthorizeStartedRef.current === oauthRequestKey
                || oauthResolveInFlightRef.current === oauthRequestKey
            ) {
                return;
            }

            autoAuthorizeStartedRef.current = oauthRequestKey;
            oauthResolveInFlightRef.current = oauthRequestKey;
            void resolveRedirectUrl('fallback').then((nextRedirectUrl) => {
                if (nextRedirectUrl) {
                    return continueToBustly(nextRedirectUrl);
                }
                return undefined;
            }).finally(() => {
                if (oauthResolveInFlightRef.current === oauthRequestKey) {
                    oauthResolveInFlightRef.current = null;
                }
            });
            return;
        }

        if (!oauthContext && !redirectUrl) {
            setIsLoading(false);
            return;
        }

        if (!oauthContext || !workspaceId || redirectUrl || error) {
            return;
        }

        console.log('[useBustlyDesktopOpen] Resolving OAuth redirect URL for success page', {
            workspaceId,
            loginTraceId: oauthContext.login_trace_id,
            redirectUri: oauthContext.redirect_uri,
            fallbackRedirectUri: oauthContext.fallback_redirect_uri,
            disableCloudRuntime,
        });
        void resolveRedirectUrl('fallback');
    }, [disableCloudRuntime, enableAutomaticFlow, error, oauthContext, oauthRequestKey, redirectUrl, workspaceId]);

    useEffect(() => {
        if (!enableAutomaticFlow || disableCloudRuntime) {
            return;
        }

        if (redirectUrl) {
            setIsLoading(false);
        }
    }, [disableCloudRuntime, enableAutomaticFlow, redirectUrl]);

    useEffect(() => {
        if (!enableAutomaticFlow || disableCloudRuntime) {
            return;
        }

        if (!redirectUrl || autoRedirectStartedRef.current) {
            return;
        }

        autoRedirectStartedRef.current = true;
        void continueToBustly(redirectUrl);
    }, [disableCloudRuntime, enableAutomaticFlow, redirectUrl]);

    return {
        disableCloudRuntime,
        error,
        handleOpenBustlyClick,
        isLoading,
        oauthContext,
        oauthRequestKey,
        redirectUrl,
        setError,
        shouldUseContinuationFlow,
        workspaceId,
    };
};
